import { useEffect, useRef } from 'react';
import { trackVisitorEvent } from '../services/visitorTracking';

const SESSION_STORAGE_KEY = 'mts.card-impressions.v1';
const seenImpressions = new Set();
let loadedFromSession = false;

function loadSeenImpressions() {
  if (loadedFromSession || typeof window === 'undefined') {
    return;
  }

  loadedFromSession = true;

  try {
    const raw = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return;

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return;

    parsed.forEach((value) => {
      if (typeof value === 'string' && value) {
        seenImpressions.add(value);
      }
    });
  } catch {
    // Non-critical analytics cache hydration.
  }
}

function persistSeenImpressions() {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(Array.from(seenImpressions)));
  } catch {
    // Non-critical analytics cache persistence.
  }
}

function buildImpressionKey({ listingId, section, variant, index }) {
  const safeListingId = listingId || 'unknown-listing';
  const safeSection = section || 'unknown-section';
  const safeVariant = variant || 'unknown-variant';
  const safeIndex = Number.isInteger(index) ? String(index) : 'na';

  return `${safeListingId}|${safeSection}|${safeVariant}|${safeIndex}`;
}

export function useListingCardImpression({
  listingId,
  context,
  section,
  index,
  tier,
  variant,
  enabled = true,
  threshold = 0.5,
}) {
  const ref = useRef(null);
  const trackedRef = useRef(false);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const target = ref.current;
    if (!target || typeof window === 'undefined') {
      return undefined;
    }

    loadSeenImpressions();

    const impressionKey = buildImpressionKey({ listingId, section, variant, index });
    if (trackedRef.current || seenImpressions.has(impressionKey)) {
      trackedRef.current = true;
      return undefined;
    }

    const metadata = {
      context: context || section || null,
      section,
      index,
      tier,
      variant,
      pathname: window.location.pathname,
    };

    const fireImpression = () => {
      if (trackedRef.current) return;
      trackedRef.current = true;
      seenImpressions.add(impressionKey);
      persistSeenImpressions();

      trackVisitorEvent({
        eventType: 'listing.card.impression',
        listingId: listingId || null,
        metadata,
      });
    };

    if (!('IntersectionObserver' in window)) {
      fireImpression();
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry || !entry.isIntersecting) return;
        if (entry.intersectionRatio < threshold) return;

        fireImpression();
        observer.disconnect();
      },
      {
        threshold: [threshold],
      }
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [context, enabled, index, listingId, section, threshold, tier, variant]);

  return ref;
}

export default useListingCardImpression;
